import type { VerificationTranslations } from "../interfaces/verification";

export const verificationTranslations: VerificationTranslations = {
  // Shell / stepper / shared actions
  step_intro: "Início",
  step_consent: "Consentimento",
  step_data: "Dados",
  step_company: "Empresa",
  step_capture: "Captura",
  step_result: "Resultado",
  stepper_progress: "{{current}}/{{total}}",
  trust_footer: "Verificação segura e criptografada pela Coloca",
  button_start: "Começar",
  button_continue: "Continuar",
  button_back: "Voltar",
  button_retry: "Tentar de novo",
  button_close: "Fechar",
  button_continue_later: "Continuar mais tarde",

  // Intro — KYC (person)
  kyc_intro_title: "Verifique sua identidade",
  kyc_intro_subtitle: "É rápido e seguro. Leva cerca de 2 minutos.",
  kyc_intro_needs_title: "Você vai precisar de:",
  kyc_intro_need_document: "Seu documento de identidade",
  kyc_intro_need_selfie: "Tirar uma selfie",
  kyc_intro_security:
    "Seus dados são criptografados e não são compartilhados com terceiros sem sua permissão.",
  kyc_intro_cta: "Começar",

  // Intro — KYB (company)
  kyb_intro_title: "Verifique sua empresa",
  kyb_intro_subtitle: "Leva entre 5 e 7 minutos.",
  kyb_intro_needs_title: "Você vai precisar de:",
  kyb_intro_need_company: "Os dados fiscais da empresa e de seu representante legal",
  kyb_intro_need_documents:
    "Tenha em mãos o contrato social e o RUT/Tax ID; o verificador irá solicitá-los durante a captura.",
  kyb_intro_save_note: "Você pode salvar e continuar depois.",
  kyb_intro_cta: "Começar",

  // Resume banner
  resume_title: "Você tem uma verificação em andamento",
  resume_description: "Retomamos exatamente de onde você parou.",
  resume_cta: "Continuar",

  // Consent
  consent_title: "Seu consentimento",
  consent_kyc_required: "Autorizo o tratamento dos meus dados biométricos para verificar minha identidade.",
  consent_kyb_required: "Declaro estar autorizado a representar a empresa.",
  consent_terms_label: "Aceito os termos e condições.",
  consent_terms_link: "Ver termos e condições",
  consent_terms_new_tab: "(abre em uma nova aba)",
  consent_required: "Marque esta caixa para continuar.",
  consent_cta: "Aceito e continuo",

  // Data (KYC) + confirm variant
  data_title: "Dados do documento",
  data_subtitle: "Confirme que coincidem com seu documento de identidade.",
  confirm_title: "Estes dados estão corretos?",
  confirm_cta_confirm: "Confirmar e continuar",
  confirm_cta_edit: "Editar dados",
  confirm_field_name: "Nome",
  confirm_field_country: "País",
  confirm_field_document_type: "Tipo de documento",

  // Company (KYB) + dedupe variant
  company_title: "Dados da empresa",
  company_block_fiscal: "Identidade fiscal",
  company_block_fiscal_badge: "Validada e compartilhada",
  company_block_representative: "Representante legal",
  company_block_contact: "Contato",
  company_dedupe_note: "País, tipo fiscal e número identificam a empresa de forma única.",
  company_verified_title: "Esta empresa já está registrada e verificada",
  company_verified_description: "Reutilizamos sua identidade fiscal; estes campos ficam bloqueados.",
  company_verified_cta_documents: "Ver documentos",
  company_verified_cta_back: "Voltar ao formulário",

  // Capture — KYC (single embedded session: document + selfie/liveness)
  capture_doc_title: "Verifique sua identidade",
  capture_doc_instruction: "Siga as instruções para fotografar seu documento e tirar uma selfie.",
  capture_doc_hint_light: "Boa iluminação",
  capture_doc_hint_complete: "Documento e rosto visíveis",
  capture_doc_hint_glare: "Sem reflexos",

  // Capture — company
  capture_company_title: "Verificação da empresa",
  capture_company_instruction:
    "O verificador solicitará o contrato social, o RUT/Tax ID e o que o nível exigir.",
  capture_company_hint_incorporation: "Contrato social",
  capture_company_hint_taxid: "RUT / Tax ID",

  // Capture — states
  capture_loading: "Iniciando verificação…",
  capture_error_title: "Não foi possível iniciar a câmera",
  capture_error_description: "Verifique sua conexão e tente novamente.",
  capture_denied_title: "Permissão da câmera bloqueada",
  capture_denied_description: "Habilite o acesso à câmera no seu navegador para continuar.",
  capture_action_retry: "Tentar de novo",
  capture_action_allowed: "Já permiti",

  // Submit (KYB ready-to-send variant)
  submit_title: "Pronto para enviar",
  submit_summary_company: "Empresa",
  submit_summary_taxid: "NIT / Tax ID",
  submit_summary_representative: "Representante legal",
  submit_cta: "Enviar para revisão",
  submit_cta_back: "Voltar à captura",

  // Processing
  processing_kyc_title: "Estamos verificando sua identidade…",
  processing_kyb_title: "Estamos verificando sua empresa…",
  processing_note: "Você pode fechar esta janela; avisaremos por e-mail quando estiver pronta.",

  // Result
  result_verified_title: "Verificação concluída",
  result_verified_description: "Tudo pronto para continuar.",
  result_verified_description_external:
    "Sua verificação foi registrada. Quem a solicitou receberá a confirmação.",
  result_verified_cta: "Ir para o início",
  result_verified_cta_done: "Pronto",
  result_verified_redirect_hint: "Levamos você ao início em {{seconds}} s.",
  result_verified_redirect_cancel: "Ficar aqui",
  result_verified_redirect_announce:
    "Levaremos você ao início em alguns segundos. Use o botão para ir agora, ou cancele o redirecionamento.",
  verified_close_window_note: "Não é preciso fazer mais nada: você já pode fechar esta janela.",

  // Welcome after approval
  welcome_eyebrow: "Verificação aprovada",
  welcome_title: "Você já pode usar a plataforma!",
  welcome_description_kyc:
    "Aprovamos sua verificação de identidade e tudo o que dependia dela já está liberado.",
  welcome_description_kyb:
    "Aprovamos a verificação da sua empresa e tudo o que dependia dela já está liberado.",
  welcome_choose: "Por onde você quer começar?",
  welcome_shortcuts_label: "Atalhos",
  welcome_close: "Fechar",
  welcome_shortcut_payins_description: "Crie uma cobrança e compartilhe com quem vai pagar você.",
  welcome_shortcut_payouts_description: "Envie dinheiro para contas bancárias e chaves.",
  welcome_shortcut_cards_description: "Emita cartões para sua equipe ou seus clientes.",
  welcome_shortcut_wallets_description: "Consulte seus saldos e movimentações em cada moeda.",
  welcome_shortcut_security_title: "Segurança",
  welcome_shortcut_security_description:
    "Escolha como você vai confirmar pagamentos e movimentações de cartões.",
  welcome_shortcut_crypto_description: "Compre USDT com o saldo da sua carteira em pesos.",
  welcome_shortcut_payout_batches_description: "Pague várias pessoas de uma vez com uma planilha.",
  welcome_shortcut_clients_description: "Cadastre as pessoas e empresas com quem você opera.",
  result_review_title: "Estamos revisando sua verificação",
  result_review_description: "Avisaremos por e-mail quando tivermos o resultado.",
  result_review_cta: "Voltar",
  result_rejected_title: "Não foi possível concluir a verificação",
  result_rejected_description: "Verifique o motivo e tente novamente.",
  result_rejected_cta: "Tentar verificação novamente",
  result_expired_title: "Este link expirou",
  result_expired_description: "Solicite um novo para continuar.",
  result_expired_cta: "Solicitar um novo",

  // Transversal states
  invalid_title: "Link inválido",
  invalid_description: "O link de verificação é inválido ou não está mais disponível.",
  already_title: "Você já está verificado",
  already_description: "Sua verificação foi concluída em {{date}}.",
  mobile_title: "Continue no seu celular",
  mobile_description: "Escaneie o código para concluir a captura no seu celular.",
  error_generic_title: "Algo deu errado",
  error_generic_description: "Não foi possível concluir a operação. Tente novamente.",

  // Rejection reasons
  rejection_document_quality: "A foto do documento não está nítida. Tente novamente com boa iluminação.",
  rejection_document_invalid: "O documento é inválido ou não foi reconhecido.",
  rejection_document_incomplete: "Faltam dados do documento. Envie o documento completo.",
  rejection_selfie_mismatch: "A selfie não coincidiu com o documento. Tente novamente com boa iluminação.",
  rejection_data_mismatch: "Os dados não coincidem com o documento.",
  rejection_duplicate: "Já existe uma verificação para esta identidade.",
  rejection_compliance: "Não foi possível aprovar a verificação. Entre em contato com o suporte.",
  rejection_additional_info_required: "Precisamos de informações adicionais para continuar.",
  rejection_generic: "Não foi possível concluir a verificação. Tente novamente.",

  // Documents (§6B)
  documents_title: "Documentos",
  documents_signed_note: "Os downloads usam links assinados temporários.",
  documents_download: "Baixar",
  documents_empty_title: "Ainda não há documentos",
  documents_empty_description: "Eles aparecerão quando a verificação for concluída.",

  // Onboarding (marco A)
  onboarding_title: "Verificação da conta",
  onboarding_description: "Conclua a verificação da sua conta para começar a operar.",
  banner_cta: "Verificar agora",
  banner_cta_resume: "Continuar verificação",
  banner_view_details: "Ver detalhes",
  banner_blocked_prefix: "Enquanto sua conta não estiver verificada, você não poderá:",
  banner_in_review_title: "Sua verificação está em análise",
  banner_in_review_description: "Avisaremos por e-mail assim que tivermos o resultado.",
  banner_rejected_title: "Não foi possível aprovar sua verificação",
  banner_expired_title: "Sua verificação expirou",
  banner_expired_description: "Inicie a verificação novamente para continuar operando.",

  // Onboarding — KYC vs KYB choice
  choose_title: "Como você opera na Coloca?",
  choose_description:
    "Escolha como deseja verificar sua conta. A verificação escolhida é a que habilita suas operações.",
  choose_person_title: "Como pessoa física",
  choose_person_title_mx: "Como pessoa física",
  choose_person_description: "Verificaremos sua identidade com seu documento e uma selfie (KYC).",
  choose_business_title: "Como empresa",
  choose_business_description: "Verificaremos a empresa e seu representante legal (KYB).",
  choose_note: "A escolha é feita uma só vez: fica definida ao criar a identidade.",
  choose_cta: "Continuar",
  replace_warning_title: "Você vai verificar sua conta com outra identidade",
  replace_warning_description:
    "Sua verificação atual deixará de valer. Escolha como vai operar a partir de agora; o envio de dinheiro fica bloqueado até que a nova verificação seja aprovada.",

  // INTERNAL — account verification admin
  admin_title: "Verificação de contas",
  admin_description:
    "Status de verificação de uma organização e liberação do cooldown de troca de identidade.",
  admin_scope_label: "Organização",
  admin_scope_placeholder: "Busque por nome, e-mail ou id",
  admin_scope_loading: "Carregando organizações…",
  admin_scope_empty: "Sem resultados",
  admin_scope_searching: "Buscando…",
  admin_scope_search_error: "Não foi possível buscar organizações.",
  admin_scope_catalog_error: "Não foi possível carregar as organizações.",
  admin_scope_catalog_truncated: "Mostrando as primeiras organizações; busque para chegar ao restante.",
  admin_scope_global_option: "Nenhuma organização",
  admin_scope_global_hint: "Selecione a organização que pediu a alteração.",
  admin_scope_org_hint: "Você está vendo a verificação desta organização.",
  admin_pick_org: "Escolha uma organização para ver sua verificação.",
  admin_load_error: "Não foi possível carregar a verificação desta organização.",
  admin_type_label: "Tipo",
  admin_status_label: "Status",
  admin_change_label: "Troca de identidade",
  admin_change_open: "Pode trocar de identidade agora.",
  admin_change_blocked: "Bloqueado até {{date}} (substituiu uma identidade verificada recentemente).",
  admin_waive: "Liberar cooldown",
  admin_waive_confirm_title: "Liberar o cooldown?",
  admin_waive_confirm_description:
    "A organização poderá verificar sua conta com outra identidade imediatamente. Fica registrado e operações é avisada no Discord.",
  admin_waive_cancel: "Cancelar",
  admin_waive_done: "Cooldown liberado.",
  admin_retry: "Reintentar",

  // Capability matrix
  capability_payouts_create: "Enviar dispersões",
  capability_payins_create: "Receber pagamentos",
  capability_wallets_recharge: "Recarregar carteiras",
  capability_swaps_create: "Converter entre carteiras",
  capability_cards_issue: "Emitir cartões",

  // Dispersions gate
  payouts_blocked_title: "Verifique sua conta para enviar dinheiro",
  payouts_blocked_description:
    "As dispersões são habilitadas quando a verificação da sua conta for aprovada. Você pode retomá-la de onde parou.",
  payouts_blocked_in_review_description:
    "Sua verificação está em análise. Você poderá enviar dinheiro quando for aprovada.",
  payouts_blocked_back_wallets: "Voltar às carteiras",
  payouts_blocked_start_description:
    "Escolha se opera como pessoa física ou como empresa e conclua a verificação para poder enviar dinheiro.",

  // Status pill labels
  status_pending: "Pendente",
  status_in_review: "Em revisão",
  status_verified: "Verificado",
  status_rejected: "Rejeitado",
  status_expired: "Expirado",

  // Marco C — identity verification section / documents
  verify_action: "Iniciar verificação",
  verify_dialog_title: "Verificação",
  link_generate: "Gerar link",
  link_copied: "Link copiado para a área de transferência",
  documents_report_label: "Relatório de verificação",
  photo_guide_eyebrow: "Antes de tirar as fotos",
  photo_guide_title: "É assim que suas fotos devem ficar",
  photo_guide_lead:
    "Se ficarem assim, sua verificação é aprovada sozinha. Se não, vai para revisão manual e pode levar semanas.",
  photo_guide_tip_light_title: "Boa luz",
  photo_guide_tip_light_body: "De frente e uniforme, sem flash, reflexos nem sombras.",
  photo_guide_tip_place_title: "Um lugar livre",
  photo_guide_tip_place_body: "Uma parede lisa atrás de você e uma mesa escura para o documento.",
  photo_guide_tip_document_title: "Seu documento físico",
  photo_guide_tip_document_body: "Original e dentro da validade. Não servem fotocópias nem versões digitais.",
  photo_guide_document_title: "Seu documento",
  photo_guide_document_caption:
    "Os quatro cantos à vista e tudo legível. Frente e verso em fotos separadas; o passaporte, aberto, em uma única foto.",
  photo_guide_selfie_title: "Sua selfie",
  photo_guide_selfie_caption: "Só o seu rosto, centralizado e de frente, com os olhos abertos.",
  photo_guide_example_ok: "Assim sim",
  photo_guide_example_corner: "Um canto fora da foto",
  photo_guide_example_glare: "Reflexo ou flash",
  photo_guide_example_angle: "Em ângulo ou torta",
  photo_guide_example_blurry: "Desfocada",
  photo_guide_example_contrast: "Fundo sem contraste",
  photo_guide_example_offcenter: "Descentralizada ou cortada",
  photo_guide_example_tilted: "Cabeça inclinada",
  photo_guide_example_glasses: "Com óculos ou boné",
  photo_guide_example_holding: "Com o documento na mão",
  photo_guide_example_crowd: "Outra pessoa na foto",
  photo_guide_rejected_note: "Se uma foto for rejeitada, tudo bem: tire uma nova e envie de novo.",
  photo_guide_cta: "Começar",
};
